import axios from 'axios'
import { Message } from 'element-ui'
import store from '../../stores'
import Vue from 'vue'
import { APP_API_URL } from '@/utils/constants'
import {
  API_CLIENT_OPTIONS,
  NETWORK_ERROR_MESSAGE,
  failedRequestOf,
  loggableFailure,
  logsOutOn403,
  showsErrorToast,
} from './policy'
import { installInterceptors } from './interceptors'
import { createLegacyHelpers } from './legacyHelpers'
// 오류 한 종류는 의존성 없는 모듈이 소유한다(단위 테스트가 닿아야 한다). 여기서 다시 export 하므로
// `import { HttpError } from '@/plugins/http'` 를 쓰던 화면은 그대로다.
import { HttpError } from './httpError'

export { HttpError }

const errorMessageKeys = ['message', 'errorMessage', 'msg', 'error']

const statusFallbackMessages = {
  400: '잘못된 요청입니다.',
  401: '인증 정보가 확인되지 않았습니다.',
  403: '접근 권한이 없습니다.',
  404: '요청하신 리소스를 찾을 수 없습니다.',
  408: '요청이 시간 초과되었습니다.',
  409: '요청이 충돌했습니다.',
  500: '서버 오류가 발생했습니다.',
  502: '잘못된 게이트웨이 응답입니다.',
  503: '서비스를 일시적으로 사용할 수 없습니다.',
  504: '게이트웨이 시간 초과가 발생했습니다.',
}

export const apiClient = createAxiosInstance()

export default {
  install(Vue) {
    Vue.prototype.$axios = apiClient
  },
}

function createAxiosInstance() {
  const baseURL = APP_API_URL

  console.log('baseURL', baseURL)

  // 옵션은 단위 시험과 같은 값을 쓴다(`policy.API_CLIENT_OPTIONS` — 60초·타임아웃 구분·2xx·쿠키)
  const apiClient = axios.create(Object.assign({ baseURL }, API_CLIENT_OPTIONS))

  // 순서와 판정은 `interceptors.js`·`policy.js` 가 갖고, 여기는 효과만 준다(경계)
  return installInterceptors(apiClient, {
    csrfToken: () => store.getters['auth/csrfToken'],
    // `state.route` 는 `router.afterEach` 가 커밋한 **확정된** 라우트다(라우터를 직접 import 하면 순환)
    route: () => store.state.route,
    now: () => Date.now(),
    setLoading: (loading) => store.commit('ui/setContentsLoading', loading),
    // 화살표로 감싼다 — `handleHttpResponse` 는 이 함수가 불리는 모듈 평가 시점보다 아래에서 선언된다(TDZ)
    handleResponse: (response) => handleHttpResponse(response),
    showTransportError: () => showHttpError(),
    wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  })
}

const handleHttpResponse = (response) => {
  if (!response) {
    return undefined
  }

  const { data, status } = response
  const fieldErrors = extractFieldErrors(data)

  if (status >= 400) {
    // 실패 로그가 어느 요청이었는지 말하게 방법·가린 경로를 싣는다(리뷰 N4) — 요청 본문·헤더·질의 문자열은 싣지 않는다
    const request = failedRequestOf(response.config)

    // **오류 토스트도 옵트아웃을 갖는다** — `showLoading`·`logoutOn403` 과 같은 모양이다.
    //
    // 이 앱은 4xx 를 언제나 빨간 토스트로 띄운다. 그 기본값은 옳지만, 어떤 상태 코드는 그 요청에
    // 대해 **오류가 아니다** — 배달 메뉴 `materialize` 의 409("그 사이 이미 연결됐다")가 그렇다.
    // 그때 화면은 목록을 다시 읽는 것으로 답하고 안내 톤으로 말하는데, 인터셉터의 빨간 토스트가
    // 함께 뜨면 화면이 두 말을 한다. **대체 동작을 가진 요청에만 쓴다.**
    const message = showsErrorToast(response.config) ? showHttpError(response) : extractErrorMessage(response)

    // **403 은 기본적으로 세션을 끝낸다** — 인가가 사라진 화면을 계속 쓰게 두지 않는다.
    //
    // 두 예외가 있고, 판정은 `policy.js` 의 [logsOutOn403] **한 곳**이 한다:
    //
    // - **부가 조회** (`logoutOn403:false`, §16.170). 화면의 본체가 아니라 곁가지를 읽는 요청이
    //   403 을 받는 뜻은 "이 계정은 그 곁가지를 못 본다" 이지 "이 세션이 무효다" 가 아닌데, 그것을
    //   로그아웃으로 번역하면 **그 화면 자체를 열 수 없게 된다.** 실제로 겪었다: 메뉴 관리 화면의
    //   형제 매장 선택기가 `GET /api/shop-in-shop/group/{storeCd}` 를 부르는데, 그 좁은 인가 행이
    //   아직 없는 환경에서는 사장님이 메뉴 관리를 열 때마다 로그인 화면으로 튕겼다.
    //   옵트아웃은 **호출부가 대체 동작을 갖고 있을 때만** 쓴다 — `showLoading` 과 같은 모양이다.
    // - **임베드 라우트** (POS 안의 창, `meta.layout === 'embed'`). 그 창에는 로그인 화면이 없다 —
    //   세션을 지우고 `/login` 으로 하드 이동하면 사장님은 POS 안에서 어드민 로그인 폼을 만난다.
    //   그 화면이 페이지 안 안내로 답한다. 판정 재료는 **요청 시점에** 요청 인터셉터가 config 에 찍은
    //   라우트 레이아웃 스냅샷이다(12R P10) — 응답 시점의 전역 `state.route` 를 읽으면 응답이 라우트
    //   전환을 가로지를 때 다른 요청자로 판정된다.
    //
    // 어느 갈래든 `HttpError` 는 던져진다 — 호출부가 받아 자기 대체 동작을 한다.
    if (status === 403) {
      if (logsOutOn403(response.config)) {
        store.dispatch('auth/logout')
        location.href = '/login?redirect_reason=403'
      }

      throw new HttpError({}, message, status, data, request)
    }

    if (status === 400 || status === 401 || status === 408) {
      throw new HttpError(fieldErrors, message, status, data, request)
    }

    throw new HttpError({}, message, status, data, request)
  }

  return data
}

function showHttpError(response) {
  const message = extractErrorMessage(response)

  Message.error({
    message,
    showClose: true,
    duration: 5000,
  })

  return message
}

function extractErrorMessage(response) {
  if (!response) {
    return NETWORK_ERROR_MESSAGE
  }

  const { data, status, statusText } = response

  if (typeof data === 'string') {
    const trimmed = data.trim()

    if (trimmed) {
      return trimmed
    }
  }

  if (data && typeof data === 'object') {
    for (const key of errorMessageKeys) {
      const value = data[key]

      if (typeof value === 'string') {
        const trimmed = value.trim()

        if (trimmed) {
          return trimmed
        }
      }
    }

    if (Array.isArray(data.errorsMessage)) {
      const joined = data.errorsMessage
        .map((item) => {
          if (typeof item === 'string') {
            return item.trim()
          }

          if (item && typeof item.errorMsg === 'string') {
            return item.errorMsg.trim()
          }

          return ''
        })
        .filter(Boolean)
        .join(' | ')

      if (joined) {
        return joined
      }
    }
  }

  return buildDefaultStatusMessage(status, statusText)
}

function buildDefaultStatusMessage(status, statusText) {
  if (!status) {
    return NETWORK_ERROR_MESSAGE
  }

  const fallback = statusFallbackMessages[status]

  if (fallback) {
    return `${fallback} (HTTP ${status})`
  }

  if (status >= 500) {
    return `서버 오류가 발생했습니다. (HTTP ${status})`
  }

  const base =
    typeof statusText === 'string' && statusText.trim() ? statusText.trim() : '요청 처리 중 오류가 발생했습니다.'

  return `${base} (HTTP ${status})`
}

function extractFieldErrors(data) {
  if (!data || typeof data !== 'object') {
    return undefined
  }

  if (Array.isArray(data.errorsMessage) && data.errorsMessage.length > 0) {
    return data.errorsMessage
      .filter((item) => item && item.field)
      .reduce((acc, item) => {
        acc[item.field] = item.errorMsg
        return acc
      }, {})
  }

  if (typeof data.error === 'string' && data.error.trim()) {
    return { error: data.error.trim() }
  }

  return undefined
}

/**
 * **요청 실패를 콘솔에 찍는 한 곳** — 요청 본문·헤더·응답 본문 없이 요약만 찍는다([loggableFailure], 리뷰 L3·N1). legacy 헬퍼
 * (`legacyHelpers.js` — 아홉 헬퍼가 한 자리에서 끝난다)와, 요청을 기다리다 실패를 받는 화면(로그인·매장 저장·상세·대시보드·
 * 엑셀·언어 데이터)이 모두 이것을 쓴다. 오류를
 * `console.error` 로 통째로 찍지 않는다 — axios 오류는 요청 본문(로그인 비밀번호·쿠폰 고객 번호·매장 PG 키)과 CSRF 헤더를
 * 싣는다. 요청 실패가 아닌 오류는 그대로 찍힌다(스택 보존).
 *
 * @param {*} error
 * @param {string} [label] 줄 앞의 말 — 어느 자리의 실패인가
 */
export function logHttpFailure(error, label = 'http error: ') {
  console.error(label, loggableFailure(error))
}

/**
 * **legacy 콜백 헬퍼** (`mainVue.$axios.*`) — 계약(요청 실패 = 값 `{ ok: false, error }`, 그 밖의 오류는 던짐)은
 * `legacyHelpers.js` 한 곳이 갖고, 여기는 효과만 준다(경계). 효과는 화살표로 감싼다 — `mergeErrors` 는 이 줄보다 아래에서
 * 선언된다(TDZ).
 */
export const mainVueAxios = createLegacyHelpers(apiClient, {
  readResponse: (response) => handleHttpResponse(response),
  logFailure: (error) => logHttpFailure(error),
  mergeErrors: (target, messages) => mergeErrors(target, messages),
})

export const mergeErrors = (target, httpError) => {
  if (!target || !httpError) return

  console.log('httpError', httpError)

  Object.entries(httpError).forEach(([key, value]) => {
    Vue.set(target, key, value)
  })
}
